/**
 * DOM utility type definitions
 */

import { ISupportService } from "./support";

/**
 * Offset result from element position calculations
 */
export interface ElementOffset {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

/**
 * Dimensions for hidden elements
 */
export interface HiddenDimensions {
    width: number;
    height: number;
}

/**
 * Shadow dimensions from box-shadow CSS
 */
export interface ShadowDimensions {
    left: number;
    right: number;
    bottom: number;
}

/**
 * Parsed effects object
 */
export interface ParsedEffects {
    [key: string]: string;
}

/**
 * Direction mapping for animations
 */
export interface DirectionInfo {
    reverse: string;
}

/**
 * Animation directions
 */
export const DIRECTIONS: Record<string, DirectionInfo> = {
    left: { reverse: "right" },
    right: { reverse: "left" },
    down: { reverse: "up" },
    up: { reverse: "down" },
    top: { reverse: "bottom" },
    bottom: { reverse: "top" },
    "in": { reverse: "out" },
    out: { reverse: "in" }
};

/**
 * Interface for DOM utility service
 */
export interface IDomUtilsService {
    /**
     * Get outer width of element
     * @param element - Element to measure
     * @param includeMargin - Whether to include margin
     * @param calculateFromHidden - Calculate width even if hidden
     */
    outerWidth(element: HTMLElement | JQuery | Window, includeMargin?: boolean, calculateFromHidden?: boolean): number;

    /**
     * Get outer height of element
     * @param element - Element to measure
     * @param includeMargin - Whether to include margin
     * @param calculateFromHidden - Calculate height even if hidden
     */
    outerHeight(element: HTMLElement | JQuery | Window, includeMargin?: boolean, calculateFromHidden?: boolean): number;

    /**
     * Get computed styles for an element
     */
    getComputedStyles(element: HTMLElement, properties?: string[]): Record<string, string>;

    /**
     * Check if an element is scrollable
     */
    isScrollable(element: HTMLElement): boolean;

    /**
     * Get or set scroll left position (RTL-aware)
     */
    scrollLeft(element: HTMLElement | JQuery, value?: number): number | void;

    /**
     * Get element offset position
     */
    getOffset(element: JQuery, type?: string, positioned?: boolean): ElementOffset;

    /**
     * Get dimensions of a hidden element by temporarily cloning and showing it
     */
    getHiddenDimensions(element: JQuery, includeMargin?: boolean): HiddenDimensions;

    /**
     * Parse effects string into object
     */
    parseEffects(input: string | string[]): ParsedEffects;

    /**
     * Remove whitespace text nodes from an element
     */
    stripWhitespace(element: HTMLElement): void;

    /**
     * Request animation frame wrapper
     */
    animationFrame(callback: FrameRequestCallback): void;

    /**
     * Queue an animation callback
     */
    queueAnimation(callback: () => void): void;

    /**
     * Run the next animation in queue
     */
    runNextAnimation(): void;

    /**
     * Parse query string parameters from URL
     */
    parseQueryStringParams(url: string): Record<string, string>;

    /**
     * Get element under cursor position
     */
    elementUnderCursor(e: { x: { client?: number }; y: { client?: number } }): Element | null;

    /**
     * Get wheel delta Y from jQuery event
     */
    wheelDeltaY(jQueryEvent: JQuery.MouseEventBase): number | undefined;

    /**
     * Get box-shadow dimensions from an element
     */
    getShadows(element: JQuery): ShadowDimensions;

    /**
     * Wrap element with animation container
     */
    wrap(element: JQuery, autosize?: boolean, resize?: boolean, shouldCorrectWidth?: boolean, autowidth?: boolean): JQuery;

    /**
     * Resize wrapped element
     */
    wrapResize(element: JQuery, autosize?: boolean, shouldCorrectWidth?: boolean): void;

    /**
     * Scroll element horizontally by a delta
     */
    scrollByDelta(element: JQuery, delta: number): ScrollResult;

    /**
     * Scroll element vertically by a delta
     */
    scrollVerticalByDelta(element: JQuery, delta: number, options?: VerticalScrollOptions): VerticalScrollResult;

    /**
     * Smoothly scroll to a specific element within a container
     */
    scrollToElement(container: JQuery, targetElement: JQuery, options?: ScrollToElementOptions): boolean;

    /**
     * Add a value to an element's attribute, avoiding duplicates
     * @param element - jQuery element
     * @param attribute - Attribute name
     * @param value - Value to add
     */
    addAttribute(element: JQuery, attribute: string, value: string): void;

    /**
     * Remove an attribute from an element
     * @param element - jQuery element
     * @param attribute - Attribute name to remove
     */
    removeAttribute(element: JQuery, attribute: string): void;

    /**
     * Toggle an attribute value on an element
     * Adds if not present, removes if present (with special handling for disabled/readonly)
     * @param element - jQuery element
     * @param attribute - Attribute name
     * @param value - Value to toggle
     */
    toggleAttribute(element: JQuery, attribute: string, value: string): void;

    /**
     * Bind a callback to the window resize event.
     * On Android, the callback is delayed by 600ms.
     * @param callback - Function to call on resize
     * @returns The handler function (may be wrapped)
     */
    onResize(callback: () => void): () => void;

    /**
     * Unbind a resize callback from the window.
     * @param callback - The handler returned from onResize
     */
    unbindResize(callback: () => void): void;

    /**
     * Get a data attribute value from an element using kendo namespace.
     * @param element - jQuery element
     * @param key - Attribute key (without kendo namespace prefix)
     * @returns The attribute value
     */
    attrValue(element: JQuery, key: string): any;

    /**
     * Get the kendo data attribute name with namespace prefix.
     * @param value - The attribute name without prefix
     * @returns The full attribute name (e.g., "data-kendo-role" or "data-role")
     */
    attr(value: string): string;

    /**
     * Get or set element dimensions.
     * @param element - jQuery element
     * @param dimensions - Optional dimensions to set
     * @returns Object with width and height
     */
    dimensions(element: JQuery, dimensions?: { width?: number | string; height?: number | string }): { width: number; height: number };

    /**
     * Check if an object is an HTML element or document
     * @param element - Object to check
     * @returns True if element is Element or HTMLDocument
     */
    isElement(element: any): boolean;

    /**
     * Check if an event was triggered by an input element
     * @param e - Event object
     * @returns True if event target is a form input element
     */
    triggeredByInput(e: Event | { target: HTMLElement }): boolean;

    /**
     * Apply inline styles from kendo data attributes to elements
     * @param element - Container element to search within
     * @param styleProps - Array of CSS property names to apply
     */
    applyStylesFromKendoAttributes(element: JQuery, styleProps: string[]): void;

    /**
     * Show or hide a loading mask on a container element
     * @param container - Container element for the mask
     * @param toggle - Whether to show (true) or hide (false) the mask
     * @param options - Optional configuration for the mask
     */
    progress(container: JQuery, toggle: boolean, options?: ProgressOptions): void;

    /**
     * Get the actual target element from an event, handling touch events.
     * For touch events, uses document.elementFromPoint with touch coordinates.
     * @param e - Event object (mouse or touch)
     * @returns The target element
     */
    eventTarget(e: Event | JQuery.Event): EventTarget | null;

    /**
     * Create a drag-to-scroll handler for horizontal scrolling via mouse/touch drag.
     * @param scrollContainer - The element that will be scrolled
     * @param options - Configuration options including namespace and capture element
     * @returns Handler with attach() and destroy() methods
     */
    createDragToScrollHandler(scrollContainer: JQuery, options: DragToScrollOptions): DragToScrollHandler;
}

/**
 * Options for the progress/loading mask
 */
export interface ProgressOptions {
    width?: string | number;
    height?: string | number;
    top?: number;
    opacity?: boolean;
}

/**
 * Result of horizontal scroll operation
 */
export interface ScrollResult {
    atStart: boolean;
    atEnd: boolean;
}

/**
 * Result of vertical scroll operation
 */
export interface VerticalScrollResult {
    atTop: boolean;
    atBottom: boolean;
}

/**
 * Options for vertical scroll by delta
 */
export interface VerticalScrollOptions {
    duration?: string | number;
    easing?: string;
}

/**
 * Options for scrollToElement
 */
export interface ScrollToElementOptions {
    duration?: number;
    easing?: string;
    position?: 'top' | 'center' | 'bottom';
    offset?: number;
    onComplete?: () => void;
}

/**
 * Options for drag-to-scroll handler
 */
export interface DragToScrollOptions {
    namespace: string;
    captureElement?: JQuery;
    delegateSelector?: string;
}

/**
 * Drag-to-scroll handler returned by createDragToScrollHandler
 */
export interface DragToScrollHandler {
    attach(): void;
    destroy(): void;
}
